import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HJCNotification, HubspotNotification, NetvisorNotification, WebSocketResponse } from '@models/websocket-types';
import { NotificationService } from '../notification.service';
import { WebSocketErrorHandlerService } from './websocket-error-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly wsUrl = environment.websocket.url;
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 3000; // 3 seconds

  private connectionStatus = new BehaviorSubject<boolean>(false);
  private messageSubject = new Subject<WebSocketResponse>();
  private errorHandlerService = inject(WebSocketErrorHandlerService);
  private notificationService = inject(NotificationService);
  private zone = inject(NgZone);
  private translate = inject(TranslateService);

  public getWebSocketUrl(): string {
    return this.wsUrl;
  }

  public getMaxReconnectAttempts(): number {
    return this.maxReconnectAttempts;
  }

  public getReconnectDelay(): number {
    return this.reconnectDelay;
  }

  public setConnectionStatus(status: boolean): void {
    this.connectionStatus.next(status);
  }

  public getConnectionStatus(): Observable<boolean> {
    return this.connectionStatus.asObservable();
  }

  public emitMessage(message: WebSocketResponse): void {
    this.messageSubject.next(message);
  }

  public getMessages(): Observable<WebSocketResponse> {
    return this.messageSubject.asObservable();
  }

  public connect(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.reconnectAttempts = 0;
    this.connectWebSocket();
  }

  public disconnect(): void {
    this.socket?.close();
    this.socket = null;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    this.setConnectionStatus(false);
  }

  public sendMessage(message: Record<string, unknown>): void {
    const connected = this.isConnected();
    if (!connected) {
      console.warn('[WebSocketService] Not connected - dropping sendMessage call for message:', message);
      return;
    }

    try {
      const messageString = JSON.stringify(message);
      this.socket!.send(messageString);
    } catch (error) {
      console.error('[WebSocketService] Error sending WebSocket message:', error, message);
    }
  }

  public isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  private connectWebSocket(): void {
    try {
      const wsUrl = this.getWebSocketUrl();

      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => this.zone.run(() => this.handleOpen());
      this.socket.onclose = () => this.zone.run(() => this.handleClose());
      this.socket.onerror = error =>
        this.zone.run(() => {
          console.error('[WebSocketService] WebSocket error event:', error);
        });
      this.socket.onmessage = event => this.zone.run(() => this.handleMessage(event));
    } catch (error) {
      console.error('Error establishing WebSocket connection:', error);
      this.setConnectionStatus(false);
      this.handleReconnect();
    }
  }

  private handleOpen(): void {
    this.setConnectionStatus(true);
    this.reconnectAttempts = 0;
  }

  private handleClose(): void {
    this.setConnectionStatus(false);
    this.handleReconnect();
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data) as WebSocketResponse;
      this.emitMessage(data);

      if (data.status === 'error' && !this.errorHandlerService.handleErrorMessage(data)) {
        return;
      }

      this.routeMessageAndShowNotifications(data);
    } catch (error) {
      console.error('Error parsing WebSocket message:', error, event.data);
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.WEBSOCKET_PARSE_ERROR'));
    }
  }

  private routeMessageAndShowNotifications(data: WebSocketResponse): void {
    switch (data.type) {
      case 'hubspot-notification':
        this.handleIntegrationNotification(data as HubspotNotification);
        break;
      case 'netvisor-notification':
        this.handleIntegrationNotification(data as NetvisorNotification);
        break;
      case 'hjc-notification':
        this.handleIntegrationNotification(data as HJCNotification);
        break;
      case 'helos-notification':
        this.handleIntegrationNotification(data as HJCNotification); // Reuse HJC type structure
        break;
      case 'event-notification':
        // Check if this is actually a helos message that should be handled
        if (data.source === 'helos' || (data.source && data.source.toLowerCase().includes('helos'))) {
          this.handleIntegrationNotification(data as HJCNotification);
        }
        break;
      default:
        console.log(`Unhandled message type by WebSocketService: ${data.type}`);
        break;
    }
  }

  private handleIntegrationNotification(notification: HubspotNotification | NetvisorNotification | HJCNotification): void {
    if (notification.status === 'error') {
      return;
    }
    const notificationData = Array.isArray(notification.data) && notification.data.length > 0 ? notification.data[0] : undefined;
    const integrationName = this.getIntegrationDisplayName(notification.source);

    // hubspot company create/update notification
    if (notification.type === 'hubspot-notification' && notificationData && 'company' in notificationData && notificationData.company) {
      const actionKey =
        'action' in notificationData && notificationData.action === 'create' ? 'NOTIFICATIONS.ACTIONS.CREATED' : 'NOTIFICATIONS.ACTIONS.UPDATED';
      const action = this.translate.instant(actionKey);
      const companyName = notificationData.company.name?.fi || `(ID: ${notificationData.company.id})`;
      const message = this.translate.instant('NOTIFICATIONS.INFO.INTEGRATION_COMPANY_ACTION', {
        integration: integrationName,
        action,
        company: companyName
      });
      this.notificationService.info(message);
      return;
    }

    // generic integration status if present
    if (notificationData && 'status' in notificationData && notificationData.status) {
      const message = this.translate.instant('NOTIFICATIONS.INFO.INTEGRATION_STATUS', {
        integration: integrationName,
        status: notificationData.status
      });
      this.notificationService.info(message);
      return;
    }

    // fallback: processed
    const message = this.translate.instant('NOTIFICATIONS.INFO.INTEGRATION_PROCESSED', { integration: integrationName });
    this.notificationService.info(message);
  }

  private getIntegrationDisplayName(source: string): string {
    const sourceMap: Record<string, string> = {
      hubspot: 'HubSpot',
      netvisor: 'Netvisor',
      hjc: 'HJC'
    };
    return sourceMap[source.toLowerCase()] || source.charAt(0).toUpperCase() + source.slice(1);
  }

  private handleReconnect(): void {
    const maxReconnectAttempts = this.getMaxReconnectAttempts();
    const reconnectDelay = this.getReconnectDelay();

    if (this.reconnectAttempts < maxReconnectAttempts) {
      this.reconnectTimeoutId = setTimeout(() => {
        this.reconnectAttempts++;
        this.connectWebSocket();
      }, reconnectDelay);
    } else {
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.WEBSOCKET_CONNECT_FAILED'));
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
